import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import L from 'leaflet';

const SERVICE_AREA_STYLE = {
  color: '#f28c00',
  weight: 2,
  opacity: 0.9,
  fillColor: '#ffd200',
  fillOpacity: 0.12,
  dashArray: '2, 6',
};

const LEGACY_SERVICE_AREA_COLORS = new Set(['#7b1fa2', '#7B1FA2']);

const normalizeServiceAreaStyle = (style = {}) => {
  const normalized = { ...SERVICE_AREA_STYLE, ...(style || {}) };
  if (LEGACY_SERVICE_AREA_COLORS.has(normalized.color)) normalized.color = SERVICE_AREA_STYLE.color;
  if (LEGACY_SERVICE_AREA_COLORS.has(normalized.fillColor)) normalized.fillColor = SERVICE_AREA_STYLE.fillColor;
  return normalized;
};

/**
 * Loads and renders Service Areas for a jobsite — a purely visual overlay
 * ("where we actually service") independent of the Area/takeoff model.
 */
export default function useServiceAreas({ url, jobsite, mapRef, visible = true, redrawKey }) {
  const [areas, setAreas] = useState([]);
  const layerGroupRef = useRef(null);

  useEffect(() => {
    return () => {
      const map = mapRef.current;
      if (map && layerGroupRef.current) {
        try { map.removeLayer(layerGroupRef.current); } catch (_) {}
      }
      layerGroupRef.current = null;
    };
  }, [mapRef]);

  const load = useCallback(async () => {
    if (!url || !jobsite?._id) {
      setAreas([]);
      return;
    }
    try {
      const resp = await axios.get(
        `${url}/api/customer/service-areas?jobSiteId=${jobsite._id}`
      );
      if (resp.data?.success && Array.isArray(resp.data.areas)) {
        setAreas(resp.data.areas);
      } else {
        setAreas([]);
      }
    } catch (err) {
      console.error('Error loading service areas:', err);
      setAreas([]);
    }
  }, [url, jobsite]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    // (Re)attach the layer group lazily: MapContainer unmounts and recreates the
    // Leaflet map when switching to the Estimate tab and back, so a group created
    // once at mount would be left on the destroyed map. redrawKey (activeMode)
    // re-runs this effect after such remounts.
    let group = layerGroupRef.current;
    if (!group || !map.hasLayer(group)) {
      group = new L.FeatureGroup();
      map.addLayer(group);
      layerGroupRef.current = group;
    }
    group.clearLayers();

    if (!visible) return;

    areas.forEach((sa) => {
      if (!sa.paths?.length || sa.paths.length < 3 || sa.isHidden) return;
      const coords = sa.paths.map(p => [p.lat, p.lng]);
      const layer = L.polygon(coords, normalizeServiceAreaStyle(sa.style));
      layer.bindTooltip(sa.name || 'Service Area');
      group.addLayer(layer);
    });
  }, [areas, visible, mapRef, redrawKey]);

  const saveArea = useCallback(async (paths, name) => {
    if (!url || !jobsite?._id || !paths || paths.length < 3) return null;
    try {
      const resp = await axios.post(
        `${url}/api/customer/service-areas`,
        { jobSiteId: jobsite._id, name: name || 'Service Area', paths }
      );
      if (resp.data?.success && resp.data?.area) {
        setAreas(prev => [...prev, resp.data.area]);
        return resp.data.area;
      }
    } catch (err) {
      console.error('saveArea error:', err);
    }
    return null;
  }, [url, jobsite]);

  const deleteArea = useCallback(async (areaId) => {
    if (!url || !areaId) return;
    try {
      await axios.delete(`${url}/api/customer/service-areas/${areaId}`);
      setAreas(prev => prev.filter(sa => sa._id !== areaId));
    } catch (err) {
      console.error('deleteArea error:', err);
    }
  }, [url]);

  const renameArea = useCallback(async (areaId, newName) => {
    if (!url || !areaId) return;
    try {
      const resp = await axios.put(
        `${url}/api/customer/service-areas/${areaId}`,
        { name: newName }
      );
      if (resp.data?.success) {
        setAreas(prev => prev.map(sa => sa._id === areaId ? { ...sa, name: newName } : sa));
      }
    } catch (err) {
      console.error('renameArea error:', err);
    }
  }, [url]);

  const updateAreaPaths = useCallback(async (areaId, newPaths) => {
    if (!url || !areaId || !newPaths || newPaths.length < 3) return;
    try {
      const resp = await axios.put(
        `${url}/api/customer/service-areas/${areaId}`,
        { paths: newPaths }
      );
      if (resp.data?.success) {
        setAreas(prev => prev.map(sa => sa._id === areaId ? { ...sa, paths: newPaths } : sa));
      }
    } catch (err) {
      console.error('updateAreaPaths error:', err);
    }
  }, [url]);

  return { areas, saveArea, deleteArea, renameArea, updateAreaPaths, reload: load };
}
